import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import clsx from 'clsx'
import Dropdown, { IOption } from 'components/Dropdown/Dropdown'
import { endOfDay, format, isValid, parseISO, startOfDay } from 'date-fns'
import Loader from 'components/Loader/Loader'
import TablePagination from 'components/TablePagination/TablePagination'
import Title from 'components/Title/Title'
import { useFmsNotificationsContext } from 'context/FmsNotificationsContext'
import { useUrlQueryState } from 'hooks/useUrlQueryState'
import { useUser } from 'hooks/user'
import { useFitPageSize } from 'hooks/useFitPageSize'
import { useCallback, useEffect, useMemo, useRef, useState, type SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  type NotificationHistoryItem,
  fetchNotificationEventsPage,
  fetchNotificationFilterAssets,
  type NotificationFilterAsset,
  markAllNotificationsReadOnServer,
} from 'services/notificationInbox'
import { fetchNotificationPrefsMeta } from 'services/notificationPrefs'
import { buildNotificationReadContext } from 'utils/notificationClient'
import {
  formatNotificationRobotCell,
  formatNotificationTimeParts,
  notifyGroupDisplayName,
} from 'utils/notificationDisplay'
import { PATH } from 'utils/constants'
import { FAVORITES_DROPDOWN_VALUE } from 'utils/favoritesSort'
import { applyStateAction, flagParam, pageParam, stringParam } from 'utils/urlQueryState'
import getTestId from 'utils/getTestId'

import style from 'components/FmsNotifications/NotificationsHistoryModal.module.css'
import panelStyle from './AccountNotificationsHistory.module.css'

function severityClass(severity: string): string {
  if (severity === 'error') return style.severityError
  if (severity === 'warning') return style.severityWarn
  if (severity === 'ok') return style.severityOk
  return style.severityInfo
}

function DatetimeCell({ value, className }: { value?: string | null; className: string }) {
  const parts = formatNotificationTimeParts(value ?? undefined)
  return (
    <span className={clsx(style.tableCol, style.datetimeCol, className)}>
      {parts ? (
        <>
          <span className={style.datetimeLine}>{parts.date}</span>
          <span className={style.datetimeLine}>{parts.time}</span>
        </>
      ) : (
        '—'
      )}
    </span>
  )
}

function RobotOrStationCell({ item }: { item: NotificationHistoryItem }) {
  const navigate = useNavigate()
  const label = formatNotificationRobotCell(item)
  const robotId = item.robot_id
  const canOpenRobot =
    robotId != null &&
    robotId > 0 &&
    item.station_pk == null &&
    !(item.station_id || '').trim()

  if (!canOpenRobot) {
    return <span className={clsx(style.tableCol, style.colRobot)}>{label}</span>
  }

  return (
    <span className={clsx(style.tableCol, style.colRobot)}>
      <button
        type="button"
        className={style.assetPlainLink}
        onClick={() => navigate(`${PATH.ROBOT}${robotId}`)}
      >
        {label}
      </button>
    </span>
  )
}

function HistoryRow({ item }: { item: NotificationHistoryItem }) {
  return (
    <div className={style.tableRow} data-fit-page-row="">
      <span className={clsx(style.severityStripe, severityClass(item.severity))} aria-hidden />
      <DatetimeCell value={item.created_at} className={style.colTime} />
      <DatetimeCell value={item.read_at} className={style.colReadAt} />
      <span className={clsx(style.tableCol, style.colTitle)}>{item.title?.trim() || '—'}</span>
      <span className={clsx(style.tableCol, style.colDetails)}>{item.body?.trim() || '—'}</span>
      <RobotOrStationCell item={item} />
      <span className={clsx(style.tableCol, style.colTask)}>
        {item.odyssey_id != null ? `#${item.odyssey_id}` : '—'}
      </span>
    </div>
  )
}

const HISTORY_DATE_PICKER_SLOT_PROPS = {
  actionBar: { actions: ['clear'] as Array<'clear'> },
  popper: {
    sx: {
      '& .MuiPaper-root': {
        background: 'var(--glass-bg) !important',
        backdropFilter: 'var(--glass-blur) !important',
        WebkitBackdropFilter: 'var(--glass-blur) !important',
        borderRadius: 'var(--radius-lg) !important',
        boxShadow: 'var(--glass-shadow) !important',
        border: 'var(--glass-border) !important',
        overflow: 'hidden !important',
      },
    },
  },
  layout: {
    sx: {
      '& .MuiPaper-root, & .MuiPickersLayout-root': {
        background: 'transparent !important',
        color: 'var(--text-main) !important',
        borderRadius: 'var(--radius-lg) !important',
        fontFamily: 'var(--font-main)',
      },
      '& .MuiTypography-root, & .MuiPickersDay-root, & .MuiButtonBase-root': {
        fontFamily: 'var(--font-main)',
        color: 'var(--text-main) !important',
      },
      '& .MuiPickersDay-today': {
        border: '1px solid var(--color-info) !important',
      },
      '& .MuiPickersDay-root.Mui-selected': {
        backgroundColor: 'var(--color-info) !important',
        color: 'white !important',
        borderRadius: 'var(--radius-lg) !important',
      },
      '& .MuiPickersDay-root:hover': {
        backgroundColor: 'var(--glass-bg-hover) !important',
      },
      '& .MuiPickersCalendarHeader-label, & .MuiDayCalendar-weekDayLabel': {
        color: 'var(--text-secondary) !important',
      },
      '& .MuiButton-root': {
        color: 'var(--color-info) !important',
        fontFamily: 'var(--font-main)',
        textTransform: 'none',
      },
    },
  },
  textField: {
    size: 'small' as const,
    sx: {
      '& .MuiInputBase-root': {
        height: 36,
        width: 148,
        borderRadius: 'var(--radius-lg)',
        backgroundColor: 'var(--solid-bg)',
        boxShadow: 'var(--glass-shadow)',
        color: 'var(--text-main)',
        fontSize: '14px',
        fontFamily: 'var(--font-main)',
        transition: 'all 0.2s ease',
        '& fieldset': {
          border: 'var(--glass-border) !important',
        },
        '&:hover fieldset': {
          border: 'var(--glass-border) !important',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#6e8efb !important',
          borderWidth: '1px !important',
        },
        '&.Mui-focused': {
          boxShadow: '0 0 0 4px rgba(110, 142, 251, 0.1)',
        },
      },
      '& .MuiInputBase-input': {
        padding: '0 12px',
        height: 36,
        boxSizing: 'border-box',
        fontFamily: 'var(--font-main)',
        '&::placeholder': {
          color: 'var(--text-secondary)',
          opacity: 1,
        },
      },
      '& .MuiSvgIcon-root': {
        color: 'var(--text-secondary)',
        fontSize: '1.2rem',
      },
    },
  },
}

function parseDateParam(value: string): Date | null {
  if (!value) return null
  const parsed = parseISO(value)
  return isValid(parsed) ? parsed : null
}

/** Inclusive local-day bounds. Swaps inverted From/To so the range still matches. */
function notificationCreatedAtBounds(from: string, to: string): {
  createdAtAfter?: string
  createdAtBefore?: string
} {
  let startDate = parseDateParam(from)
  let endDate = parseDateParam(to)
  if (startDate && endDate && startDate.getTime() > endDate.getTime()) {
    const swapped = startDate
    startDate = endDate
    endDate = swapped
  }
  return {
    createdAtAfter: startDate ? startOfDay(startDate).toISOString() : undefined,
    createdAtBefore: endDate ? endOfDay(endDate).toISOString() : undefined,
  }
}

function parseAssetFilter(value: string): { robotId?: number; stationPk?: number } {
  if (!value) return {}
  if (value.startsWith('r:')) {
    const id = Number(value.slice(2))
    return id > 0 ? { robotId: id } : {}
  }
  if (value.startsWith('s:')) {
    const id = Number(value.slice(2))
    return id > 0 ? { stationPk: id } : {}
  }
  return {}
}

/** Notifications history as an Account cabinet panel (not a modal). */
const AccountNotificationsHistory = () => {
  const { t } = useTranslation()
  const user = useUser()
  const { markAllRead } = useFmsNotificationsContext()
  const [url, setUrl] = useUrlQueryState({
    q: stringParam(),
    page: pageParam(),
    group: stringParam(),
    errorGroup: stringParam(),
    asset: stringParam(),
    afav: flagParam(),
    from: stringParam(),
    to: stringParam(),
  })
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [rows, setRows] = useState<NotificationHistoryItem[]>([])
  const [total, setTotal] = useState(0)
  const page = url.page
  const setPage = (value: SetStateAction<number>) =>
    setUrl(s => ({ page: applyStateAction(s.page, value) }))
  const [pageInput, setPageInput] = useState(String(url.page))
  const searchText = url.q
  const setSearchText = (q: string) => setUrl({ q })
  const [debouncedSearch, setDebouncedSearch] = useState(url.q)
  const notifyGroupId = url.group
  const setNotifyGroupId = (group: string) => setUrl({ group, page: 1 })
  const errorGroup = url.errorGroup
  const setErrorGroup = (errorGroupValue: string) => setUrl({ errorGroup: errorGroupValue, page: 1 })
  const assetKey = url.asset
  const favoriteRobotIds = useMemo(() => user?.state.favoriteRobotIds ?? [], [user?.state.favoriteRobotIds])
  const favoriteStationIds = useMemo(() => user?.state.favoriteStationIds ?? [], [user?.state.favoriteStationIds])
  const favoritesOnly = url.afav && !assetKey.trim()
  const noFavoriteAssets = favoritesOnly && favoriteRobotIds.length === 0 && favoriteStationIds.length === 0
  const fromDate = url.from
  const setFromDate = (from: string) => setUrl({ from, page: 1 })
  const toDate = url.to
  const setToDate = (to: string) => setUrl({ to, page: 1 })
  const [filterAssets, setFilterAssets] = useState<NotificationFilterAsset[]>([])
  const [notifyGroupOptions, setNotifyGroupOptions] = useState<IOption[]>([])
  const [errorGroupOptions, setErrorGroupOptions] = useState<IOption[]>([])
  const tableBodyRef = useRef<HTMLDivElement>(null)
  const loadRequestIdRef = useRef(0)
  const pageSize = useFitPageSize(tableBodyRef, {
    page,
    layoutKey: rows.length,
    useCssRowHeight: true,
    onPageChange: nextPage => setUrl(s => ({ page: nextPage })),
  })

  const totalPages = Math.max(1, Math.ceil(total / Math.max(pageSize, 1)))

  const loadPage = useCallback(
    async (
      targetPage: number,
      search: string,
      groupId: string,
      asset: string,
      diagnosticGroup: string,
      from: string,
      to: string,
      size: number
    ) => {
      if (size < 1) return
      const requestId = ++loadRequestIdRef.current
      setLoading(true)
      try {
        const { robotId, stationPk } = parseAssetFilter(favoritesOnly ? '' : asset)
        const parsedGroupId = groupId ? Number(groupId) : undefined
        const { createdAtAfter, createdAtBefore } = notificationCreatedAtBounds(from, to)
        const data = await fetchNotificationEventsPage({
          page: targetPage,
          pageSize: size,
          search,
          notifyGroupId: parsedGroupId && parsedGroupId > 0 ? parsedGroupId : undefined,
          robotId: favoritesOnly ? undefined : robotId,
          stationPk: favoritesOnly ? undefined : stationPk,
          robotIds: favoritesOnly ? favoriteRobotIds : undefined,
          stationPks: favoritesOnly ? favoriteStationIds : undefined,
          errorGroup: diagnosticGroup || undefined,
          createdAtAfter,
          createdAtBefore,
        })
        // Drop stale responses: first filter click often races the in-flight unfiltered load.
        if (requestId !== loadRequestIdRef.current) return
        setRows(data.results)
        setTotal(data.total)
        setUrl(s => ({ page: data.page_number }))
        setPageInput(String(data.page_number))
      } catch (err) {
        if (requestId !== loadRequestIdRef.current) return
        console.warn('[Notifications] history load failed', err)
        setRows([])
        setTotal(0)
      } finally {
        if (requestId === loadRequestIdRef.current) {
          setLoading(false)
          setInitialLoading(false)
        }
      }
    },
    [setUrl, favoritesOnly, favoriteRobotIds, favoriteStationIds]
  )

  const skipSearchPageResetRef = useRef(true)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText)
      if (skipSearchPageResetRef.current) {
        skipSearchPageResetRef.current = false
        return
      }
      if (page !== 1) {
        setPage(1)
        setPageInput('1')
      }
    }, 300)
    return () => clearTimeout(timer)
    // Debounce search only; do not reset the timer when page changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText])

  useEffect(() => {
    let cancelled = false
    void fetchNotificationFilterAssets()
      .then(results => {
        if (cancelled) return
        setFilterAssets(results)
      })
      .catch(err => {
        console.warn('[Notifications] failed to load assets for filter', err)
        if (!cancelled) {
          setFilterAssets([])
        }
      })

    void fetchNotificationPrefsMeta()
      .then(meta => {
        if (cancelled) return
        const groups = (meta.notify_groups || [])
          .map(group => ({
            value: group.id,
            text: notifyGroupDisplayName(group.name),
          }))
          .sort((a, b) => a.text.localeCompare(b.text, undefined, { sensitivity: 'base' }))
        setNotifyGroupOptions([
          { value: '', text: t('account.filter_all_notify_groups') },
          ...groups,
        ])
        setErrorGroupOptions([
          { value: '', text: t('account.filter_all_diagnostic_groups') },
          ...(meta.error_groups || []).map(g => ({ value: g, text: g })),
        ])
      })
      .catch(err => {
        console.warn('[Notifications] failed to load prefs meta for filters', err)
        if (!cancelled) {
          setNotifyGroupOptions([])
          setErrorGroupOptions([])
        }
      })

    void markAllNotificationsReadOnServer(buildNotificationReadContext('history_open'))
      .then(() => markAllRead('history_open', { skipServer: true }))
      .catch(err => console.warn('[Notifications] history mark-all-read failed', err))

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (pageSize < 1) return
    if (noFavoriteAssets) {
      setRows([])
      setTotal(0)
      setLoading(false)
      setInitialLoading(false)
      return
    }
    void loadPage(page, debouncedSearch, notifyGroupId, assetKey, errorGroup, fromDate, toDate, pageSize)
  }, [
    page,
    pageSize,
    debouncedSearch,
    notifyGroupId,
    assetKey,
    errorGroup,
    fromDate,
    toDate,
    loadPage,
    noFavoriteAssets,
  ])

  useEffect(() => {
    if (pageSize < 1 || total === 0) return
    if (page > totalPages) setUrl(s => ({ page: totalPages }))
  }, [page, pageSize, setUrl, total, totalPages])

  const assetOptions = useMemo(() => {
    const favoriteRobots = new Set(favoriteRobotIds)
    const favoriteStations = new Set(favoriteStationIds)
    const isFavorite = (asset: NotificationFilterAsset) =>
      asset.kind === 'station' ? favoriteStations.has(asset.id) : favoriteRobots.has(asset.id)
    const rows = filterAssets
      .filter(asset => !favoritesOnly || isFavorite(asset))
      .map(asset => {
        const name = asset.name?.trim() || ''
        const serial = asset.serial_number?.trim() || ''
        const value = `${asset.kind === 'station' ? 's' : 'r'}:${asset.id}`
        if (serial) {
          return {
            value,
            leadingText: serial,
            text: name && name !== serial ? name : '',
            favorite: isFavorite(asset),
          }
        }
        return { value, leadingText: '', text: name || value, favorite: isFavorite(asset) }
      })
      .sort((a, b) => {
        if (a.favorite !== b.favorite) return a.favorite ? -1 : 1
        const aKey = String(a.leadingText || a.text)
        const bKey = String(b.leadingText || b.text)
        return aKey.localeCompare(bKey, undefined, { sensitivity: 'base', numeric: true })
      })
    return [
      { value: '', text: t('account.filter_all_assets') },
      { value: FAVORITES_DROPDOWN_VALUE, text: t('favorites.only'), leadingIcon: 'star' as const },
      ...rows.map(({ favorite: _favorite, ...option }) => option),
    ]
  }, [filterAssets, favoriteRobotIds, favoriteStationIds, favoritesOnly, t])

  const currentNotifyGroupOption = useMemo(
    () => (notifyGroupId ? notifyGroupOptions.find(o => String(o.value) === notifyGroupId) : undefined),
    [notifyGroupId, notifyGroupOptions]
  )

  const isRefreshing = loading && !initialLoading

  return (
    <div className={panelStyle.panel} data-test-id={getTestId('account-notifications-history')}>
      <div className={clsx(style.sandwich, panelStyle.fill)}>
        <div className={clsx(style.titleBlock, panelStyle.titleBlock)}>
          <Title text={t('account.history_title')} />
        </div>

        <div className={clsx(style.controlsBlock, panelStyle.controlsGutter)}>
          <input
            className={style.searchInput}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            placeholder={t('common.search')}
          />
          <Dropdown
            className={style.filterDropdown}
            options={assetOptions}
            currentOption={
              assetKey
                ? assetOptions.find(o => String(o.value) === assetKey) || {
                    value: assetKey,
                    text: assetKey,
                  }
                : favoritesOnly
                  ? {
                      value: FAVORITES_DROPDOWN_VALUE,
                      text: t('favorites.only'),
                      leadingIcon: 'star' as const,
                    }
                  : assetOptions.find(o => o.value === '') || undefined
            }
            placeholder={t('account.filter_all_assets')}
            menuFitContent
            onClickOption={opt => {
              const nextAsset = opt.value != null ? String(opt.value) : ''
              if (nextAsset === FAVORITES_DROPDOWN_VALUE) {
                if (favoritesOnly) return
                setUrl({ asset: '', afav: true, page: 1 })
                setPageInput('1')
                return
              }
              if (nextAsset === assetKey && !favoritesOnly) return
              setUrl({ asset: nextAsset, afav: false, page: 1 })
              setPageInput('1')
            }}
            useSmallDropdownStyle
          />
          <Dropdown
            className={style.filterDropdown}
            options={notifyGroupOptions}
            currentOption={currentNotifyGroupOption}
            placeholder={t('account.filter_all_notify_groups')}
            menuFitContent
            onClickOption={opt => {
              const nextGroup = opt.value != null ? String(opt.value) : ''
              if (nextGroup === notifyGroupId) return
              setNotifyGroupId(nextGroup)
              setPageInput('1')
            }}
            useSmallDropdownStyle
          />
          <Dropdown
            className={style.filterDropdown}
            options={errorGroupOptions}
            currentOption={errorGroup ? errorGroupOptions.find(o => String(o.value) === errorGroup) : undefined}
            placeholder={t('account.filter_all_diagnostic_groups')}
            menuFitContent
            onClickOption={opt => {
              const nextGroup = String(opt.value ?? '')
              if (nextGroup === errorGroup) return
              setErrorGroup(nextGroup)
              setPageInput('1')
            }}
            useSmallDropdownStyle
          />
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <div className={panelStyle.dateRange}>
              <DatePicker
                value={parseDateParam(fromDate)}
                onChange={newDate => {
                  if (!newDate) {
                    setFromDate('')
                    setPageInput('1')
                    return
                  }
                  if (!isValid(newDate)) return
                  setFromDate(format(newDate, 'yyyy-MM-dd'))
                  setPageInput('1')
                }}
                format="dd/MM/yyyy"
                slotProps={{
                  ...HISTORY_DATE_PICKER_SLOT_PROPS,
                  textField: {
                    ...HISTORY_DATE_PICKER_SLOT_PROPS.textField,
                    placeholder: t('account.filter_date_from'),
                  },
                }}
              />
              <span className={panelStyle.dateSep}>–</span>
              <DatePicker
                value={parseDateParam(toDate)}
                onChange={newDate => {
                  if (!newDate) {
                    setToDate('')
                    setPageInput('1')
                    return
                  }
                  if (!isValid(newDate)) return
                  setToDate(format(newDate, 'yyyy-MM-dd'))
                  setPageInput('1')
                }}
                format="dd/MM/yyyy"
                slotProps={{
                  ...HISTORY_DATE_PICKER_SLOT_PROPS,
                  textField: {
                    ...HISTORY_DATE_PICKER_SLOT_PROPS.textField,
                    placeholder: t('account.filter_date_to'),
                  },
                }}
              />
            </div>
          </LocalizationProvider>
        </div>

        <div className={style.tableWrapper}>
          <div className={clsx(style.tableHeader, panelStyle.tableHeaderFit)}>
            <div />
            <div className={clsx(style.tableHeaderItem, style.colTime)}>Time</div>
            <div className={clsx(style.tableHeaderItem, style.colReadAt)}>Read at</div>
            <div className={clsx(style.tableHeaderItem, style.colTitle)}>Title</div>
            <div className={clsx(style.tableHeaderItem, style.colDetails)}>Details</div>
            <div className={clsx(style.tableHeaderItem, style.colRobot)}>{t('account.history_col_asset')}</div>
            <div className={clsx(style.tableHeaderItem, style.colTask)}>Task</div>
          </div>
          <div
            ref={tableBodyRef}
            className={clsx(style.tableContent, panelStyle.tableFit, isRefreshing && style.tableContentRefreshing)}
          >
            {noFavoriteAssets ? (
              <div className={style.favoritesEmpty}>
                <div className={style.favoritesEmptyTitle}>{t('favorites.empty_assets')}</div>
                <div className={style.favoritesEmptyDescription}>{t('favorites.empty_assets_description')}</div>
              </div>
            ) : initialLoading && !rows.length ? (
              <Loader />
            ) : rows.length > 0 ? (
              <div className={clsx(style.tableBody, panelStyle.tableBodyFit)}>
                {rows.map(item => (
                  <HistoryRow key={item.id} item={item} />
                ))}
              </div>
            ) : (
              <div className={style.emptyWrapper}>{t('header.no_notifications')}</div>
            )}
          </div>
        </div>

        {total > pageSize && pageSize > 0 ? (
          <div className={style.footer}>
            <TablePagination
              page={page}
              totalPages={totalPages}
              pageInput={pageInput}
              onPageInputChange={setPageInput}
              onPageInputKeyDown={e => {
                if (e.key === 'Enter') {
                  const p = parseInt(pageInput, 10)
                  if (!Number.isNaN(p) && p >= 1 && p <= totalPages) setPage(p)
                }
              }}
              onPageInputBlur={() => {
                const p = parseInt(pageInput, 10)
                if (!Number.isNaN(p) && p >= 1 && p <= totalPages) setPage(p)
                else setPageInput(String(page))
              }}
              onPrev={() => setPage(p => Math.max(1, p - 1))}
              onNext={() => setPage(p => Math.min(totalPages, p + 1))}
            />
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default AccountNotificationsHistory
